import React, { useEffect, useState } from "react";
import { HERO_SLIDES } from "@/data/site";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MapPin, Calendar, Users, Search, ChevronDown } from "lucide-react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Hero() {
  const [idx, setIdx] = useState(0);
  const [form, setForm] = useState({ destination: "", departure_date: "", return_date: "", travelers: "2" });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % HERO_SLIDES.length), 5000);
    return () => clearInterval(t);
  }, []);

  const scrollTo = (id) => {
    const el = document.querySelector(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  const submitSearch = async (e) => {
    e.preventDefault();
    if (!form.destination.trim()) {
      toast.error("Please enter a destination");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/booking-inquiry`, form);
      toast.success("Great! We'll match you with the best packages.");
      scrollTo("#packages");
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="home" data-testid="hero-section" className="relative min-h-screen w-full overflow-hidden">
      {/* Slides */}
      <div className="absolute inset-0">
        {HERO_SLIDES.map((s, i) => (
          <div
            key={s.url}
            aria-hidden={i !== idx}
            className={`absolute inset-0 bg-cover bg-center transition-opacity duration-[1500ms] ease-in-out ${i === idx ? "opacity-100" : "opacity-0"}`}
            style={{ backgroundImage: `url(${s.url})` }}
          />
        ))}
        <div className="absolute inset-0 bg-gradient-to-t from-[#0B3D91]/80 via-[#0B3D91]/30 to-black/40" />
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-7xl mx-auto px-5 md:px-8 pt-36 md:pt-44 pb-32 min-h-screen flex flex-col justify-center">
        <div className="max-w-3xl">
          <span data-testid="hero-eyebrow" className="inline-block px-4 py-1.5 rounded-full bg-white/15 border border-white/30 backdrop-blur-md text-white text-xs font-medium tracking-[0.15em] uppercase mb-6">
            Your Trusted Travel Partner
          </span>
          <h1 data-testid="hero-title" className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white leading-[1.1] tracking-tight drop-shadow-lg">
            Explore the World with <span className="text-[#F9B233]">Malviya Travel</span>
          </h1>
          <p data-testid="hero-subtitle" className="mt-6 text-base sm:text-lg text-white/90 leading-relaxed max-w-2xl">
            Discover unforgettable journeys with affordable tour packages, flight bookings, hotel reservations, visa assistance, and customized holiday experiences designed around you.
          </p>

          <div className="mt-8 flex flex-wrap gap-4">
            <Button
              data-testid="hero-explore-btn"
              onClick={() => scrollTo("#packages")}
              className="bg-[#F9B233] hover:bg-[#e6a11f] text-[#0B3D91] font-semibold rounded-full h-12 px-8 shadow-lg hover:scale-[1.03] transition-all"
            >
              Explore Packages
            </Button>
            <Button
              data-testid="hero-contact-btn"
              onClick={() => scrollTo("#contact")}
              variant="outline"
              className="bg-white/10 backdrop-blur-md border-white/40 text-white hover:bg-white hover:text-[#0B3D91] rounded-full h-12 px-8 font-semibold transition-all"
            >
              Contact Us
            </Button>
          </div>
        </div>

        {/* Booking Search Card */}
        <form
          data-testid="hero-booking-card"
          onSubmit={submitSearch}
          className="mt-14 md:mt-20 glass-white rounded-2xl shadow-[0_20px_60px_-15px_rgba(11,61,145,0.35)] p-5 md:p-6 grid grid-cols-1 md:grid-cols-5 gap-4 items-end"
        >
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide flex items-center gap-1.5">
              <MapPin className="w-3.5 h-3.5" /> Destination
            </Label>
            <Input
              data-testid="hero-search-destination"
              placeholder="Bali, Dubai, Goa..."
              value={form.destination}
              onChange={(e) => setForm({ ...form, destination: e.target.value })}
              className="border-gray-200 focus:border-[#0B3D91] focus:ring-[#0B3D91]/20 h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Departure
            </Label>
            <Input
              data-testid="hero-search-departure"
              type="date"
              value={form.departure_date}
              onChange={(e) => setForm({ ...form, departure_date: e.target.value })}
              className="border-gray-200 h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide flex items-center gap-1.5">
              <Calendar className="w-3.5 h-3.5" /> Return
            </Label>
            <Input
              data-testid="hero-search-return"
              type="date"
              value={form.return_date}
              onChange={(e) => setForm({ ...form, return_date: e.target.value })}
              className="border-gray-200 h-11"
            />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs font-semibold text-[#0B3D91] uppercase tracking-wide flex items-center gap-1.5">
              <Users className="w-3.5 h-3.5" /> Travelers
            </Label>
            <div className="relative">
              <select
                data-testid="hero-search-travelers"
                value={form.travelers}
                onChange={(e) => setForm({ ...form, travelers: e.target.value })}
                className="w-full h-11 border border-gray-200 rounded-md px-3 pr-8 text-sm bg-white appearance-none focus:outline-none focus:border-[#0B3D91]"
              >
                {[1,2,3,4,5,6,7,8].map(n => <option key={n} value={String(n)}>{n} {n===1?"Traveler":"Travelers"}</option>)}
              </select>
              <ChevronDown className="w-4 h-4 text-gray-500 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
            </div>
          </div>
          <Button
            type="submit"
            data-testid="hero-search-btn"
            disabled={loading}
            className="bg-[#0B3D91] hover:bg-[#082d6d] text-white rounded-full h-11 font-semibold flex items-center gap-2 shadow-md hover:shadow-lg transition-all"
          >
            <Search className="w-4 h-4" />
            {loading ? "Searching..." : "Search"}
          </Button>
        </form>
      </div>

      {/* Slide indicators */}
      <div className="absolute bottom-6 left-1/2 -translate-x-1/2 z-10 flex gap-2">
        {HERO_SLIDES.map((_, i) => (
          <button
            key={i}
            aria-label={`Go to slide ${i+1}`}
            onClick={() => setIdx(i)}
            className={`h-1.5 rounded-full transition-all ${i === idx ? "w-8 bg-[#F9B233]" : "w-2 bg-white/60"}`}
          />
        ))}
      </div>
    </section>
  );
}
